import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { memo, useCallback, useState } from "react";

import Button from "shared/components/button/Button.jsx";
import CenteredTitle from "shared/components/title/CenteredTitle.jsx";
import PopularSpace from "./PopularSpace.jsx";
import { SPACES_ROUTE } from 'app/navigation/routes/space-routes';
import { useNavigationContext } from 'shared/providers/NavigationProvider';
import { useTranslation } from 'react-i18next';

const HowItWorks = () => {

    const { t } = useTranslation();
    const [open, setOpen] = useState(0);

    const toggle = (idx) => setOpen(open === idx ? 0 : idx);

    return (
        <section className="px-6 flex flex-col items-center mt-12" id="how-it-works">
            <CenteredTitle
                title={t('website.howItWorks.title')}
                subtitle={t('website.howItWorks.subtitle')}
            />
            <ContentWrapper>
                {/* PopularSpace is visible in large screens only */}
                <div className="hidden lg:block w-full lg:w-1/2">
                    <PopularSpace />
                </div>

                <BookingSteps toggle={toggle} open={open} />
            </ContentWrapper>
        </section>
    );
};

export default HowItWorks;


const ContentWrapper = ({ children }) => {
    return (
        <div className="max-w-6xl w-full rounded-xl py-10 px-8 flex flex-col lg:flex-row gap-12 ">
            {children}
        </div>
    );
};

const BookingSteps = ({ toggle, open }) => {

    const { t } = useTranslation();
    const { navigateToRoute } = useNavigationContext();

    const handleNavigateToSpacesPage = useCallback(
        () => navigateToRoute(SPACES_ROUTE),
        [navigateToRoute]
    );

    return (
        <div className="w-full lg:w-1/2 items-center flex flex-col lg:items-start justify-center">
            <div className="space-y-4">
                {steps.map((step, idx) => <StepItem idx={idx} key={step.title} toggle={toggle} open={open} step={step} />)}
            </div>
            <div className="flex justify-start">
                <Button
                    onClick={handleNavigateToSpacesPage}
                    className="mt-10 bg-lime-700 text-white px-8 py-3 rounded-md shadow transition"
                >
                    <span className="flex items-center text-md">
                        {t('website.howItWorks.getStarted')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                </Button>
            </div>
        </div>
    );
};


const StepItem = memo(({ idx, toggle, open, step }) => {
    const { t } = useTranslation();

    return (
        <div key={idx} className="border-b pb-4 w-full">
            {/* I do not know why "Button" does not ensure the icon is in the same row. I'll check. */}
            <button
                onClick={() => toggle(idx)} className={`flex items-center justify-between text-xl w-full text-left min-w-[325px]  ${open === idx ? " font-semibold text-lime-700" : "font-medium text-gray-700"}`}
            >
                {t(`website.howItWorks.steps.${idx + 1}.title`)}
                {open === idx ? <ChevronUp className="text-gray-500" /> : <ChevronDown className="text-gray-500" />}
            </button>
            {
                open === idx && <p className="mt-3 text-gray-500 font-light text-md text-justify leading-relaxed">{t(`website.howItWorks.steps.${idx + 1}.description`)}</p>
            }
        </div>
    );
});

const steps = [
    {
        title: "Step 1: Discover an Ideal Space",
        description:
            "Browse our curated list of premium workspaces and choose the one that matches your needs — from private offices to cozy meeting rooms.",
    },
    {
        title: "Step 2: Easy Booking",
        description:
            "Pick your date, time, and duration. Our streamlined booking flow makes the process quick and completely hassle-free.",
    },
    {
        title: "Step 3: Secure Payment",
        description:
            "Pay safely through our integrated, encrypted gateway. We support multiple payment options for your convenience.",
    },
    {
        title: "Step 4: Enjoy Your Workspace",
        description:
            "Show up and get things done. Every space comes equipped with essential amenities and support for an exceptional experience.",
    },
];
